import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getConditionDescription } from '../../config/skinConditionDescriptions';
import DetectionCanvas from './DetectionCanvas';
import { useSegmentation } from './SegmentationRenderer';
import useSkinResultStore from '../../store/skinResultStore';
import { useReportImages } from '../../hooks/useReportImages';
import { buildDetailedConcernItems } from '../../utils/buildDetailedConcernItems';
import { drawConcernGauge } from '../../utils/concernGauge';
import { normalizeReportMasks } from '../../utils/e2eMedia';
import { resolveVisualMaskUrl } from '../../utils/maskUtils';
import { getSeverityBarLabels } from '../../utils/severityBarLabels';
import { sanitizeText } from '../../utils/sanitizeContent';
import {
  buildSeverityBarGradientStyleVars,
  isSkinConditionName,
} from '../../utils/skinResultsUtils';

const VIEWS = ['front', 'left', 'right'];

function getScoreOutlineColor(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return 'fair';
  if (n >= 0 && n <= 39) return 'critical';
  if (n >= 40 && n <= 59) return 'poor';
  if (n >= 60 && n <= 74) return 'fair';
  if (n >= 75 && n <= 89) return 'good';
  return 'excellent';
}

function severityLabel(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return '—';
  if (n <= 39) return 'Critical';
  if (n <= 59) return 'High';
  if (n <= 74) return 'Moderate';
  if (n <= 89) return 'Mild';
  return 'Healthy';
}

const DetectionViewer = ({ report, skinReference, appSessionToken }) => {
  const containerRef = useRef(null);
  const concernGridRef = useRef(null);
  const concernGaugeCopyRef = useRef(null);
  const concernGaugeOverlayRef = useRef(null);
  const touchStartRef = useRef({ x: 0, y: 0 });
  const [containerWidth, setContainerWidth] = useState(480);
  const [containerHeight, setContainerHeight] = useState(480);
  const segmentation = useSegmentation();

  const {
    imageView,
    selectedConcern,
    setImageView,
    selectConcern,
    selectScoreKey,
  } = useSkinResultStore();

  const { images, hydrating, hydrateError } = useReportImages(
    report,
    skinReference,
    appSessionToken,
  );

  const detailedItems = useMemo(
    () => buildDetailedConcernItems(report, segmentation),
    [report, segmentation],
  );

  const detectionsByView = useMemo(
    () => report?.detections || {},
    [report?.detections],
  );

  const selectedConcernItem = useMemo(
    () => detailedItems.find((item) => item.key === selectedConcern) || null,
    [detailedItems, selectedConcern],
  );

  const activeDetections = detectionsByView[imageView] || detectionsByView.front || [];
  const imageUrl = images[imageView] || images.front || images.left || images.right;

  const availableViews = useMemo(
    () => VIEWS.filter((view) => Boolean(images[view])),
    [images],
  );

  const isSkinConditionConcern = Boolean(
    selectedConcern && isSkinConditionName(selectedConcern),
  );
  const showAngleNavigation = availableViews.length > 1 && !isSkinConditionConcern;

  const selectedConcernDescription = useMemo(() => {
    if (!selectedConcernItem) return '';
    return getConditionDescription(selectedConcernItem.class_name);
  }, [selectedConcernItem]);

  const visualMasks = useMemo(
    () => normalizeReportMasks(report?.visual_masks || {}),
    [report?.visual_masks],
  );

  const resolvedMaskUrl = useMemo(
    () => resolveVisualMaskUrl(visualMasks, selectedConcern),
    [visualMasks, selectedConcern],
  );

  const selectedConcernSeverityGradientStyle = useMemo(
    () => buildSeverityBarGradientStyleVars(selectedConcernItem?.key || selectedConcern),
    [selectedConcernItem, selectedConcern],
  );

  const severityBarLabels = useMemo(
    () => getSeverityBarLabels(selectedConcernItem?.key || selectedConcern),
    [selectedConcernItem, selectedConcern],
  );

  const showMaskPreview = Boolean(
    isSkinConditionConcern && imageView === 'front' && resolvedMaskUrl,
  );

  useEffect(() => {
    if (!availableViews.includes(imageView) && availableViews.length) {
      setImageView(availableViews[0]);
    }
  }, [availableViews, imageView, setImageView]);

  const handleSelectItem = useCallback((item) => {
    if (!item) return;
    if (item.is_condition && item.scoreKey) {
      selectScoreKey(item.scoreKey);
    } else {
      selectConcern(item.key, item.view, { preferMask: item.is_condition });
    }
  }, [selectConcern, selectScoreKey]);

  useEffect(() => {
    if (!detailedItems.length || selectedConcern) return;
    handleSelectItem(detailedItems[0]);
  }, [detailedItems, selectedConcern, handleSelectItem]);

  useEffect(() => {
    const score = selectedConcernItem?.score;
    if (score == null) return;
    drawConcernGauge(concernGaugeCopyRef.current, score);
    drawConcernGauge(concernGaugeOverlayRef.current, score);
  }, [selectedConcernItem]);

  useEffect(() => {
    if (isSkinConditionConcern && imageView !== 'front') {
      setImageView('front');
    }
  }, [isSkinConditionConcern, imageView, setImageView]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;
    const ro = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (!rect) return;
      if (rect.width) setContainerWidth(Math.floor(rect.width));
      if (rect.height) setContainerHeight(Math.floor(rect.height));
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!selectedConcern || typeof window === 'undefined' || window.innerWidth > 768) return;
    const grid = concernGridRef.current;
    if (!grid) return;
    const selectedCard = grid.querySelector(`[data-concern-name="${selectedConcern}"]`);
    if (!selectedCard) return;
    requestAnimationFrame(() => {
      selectedCard.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    });
  }, [selectedConcern]);

  const moveViewBy = useCallback((direction) => {
    const idx = availableViews.indexOf(imageView);
    const current = idx >= 0 ? idx : 0;
    const next = (current + direction + availableViews.length) % availableViews.length;
    setImageView(availableViews[next]);
  }, [availableViews, imageView, setImageView]);

  const scrollConcernGrid = useCallback((direction) => {
    const grid = concernGridRef.current;
    if (!grid) return;
    grid.scrollBy({ left: direction * window.innerWidth * 0.8, behavior: 'smooth' });
  }, []);

  const handleTouchStart = useCallback((event) => {
    const touch = event.touches[0];
    if (!touch) return;
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  }, []);

  const handleTouchEnd = useCallback((event) => {
    if (availableViews.length < 2 || isSkinConditionConcern) return;
    const touch = event.changedTouches[0];
    if (!touch) return;
    const dx = touch.clientX - touchStartRef.current.x;
    const dy = touch.clientY - touchStartRef.current.y;
    if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    moveViewBy(dx > 0 ? -1 : 1);
  }, [availableViews.length, isSkinConditionConcern, moveViewBy]);

  if (!segmentation.showConcerns && !segmentation.showConditions) {
    return null;
  }

  const displayScore = selectedConcernItem?.score ?? 0;
  const scoreCategory = getScoreOutlineColor(displayScore);

  return (
    <div className="skin-routine-section-block skin-routine-concerns-block">
      <div className="skin-routine-section-heading-with-info">
        <h2 className="skin-routine-section-heading">Concerns</h2>
      </div>

      {detailedItems.length === 0 ? (
        <div className="no-conditions-message">
          <p>No significant skin conditions detected.</p>
          <p className="no-conditions-subtitle">Your skin looks healthy!</p>
        </div>
      ) : (
        <div className="skin-routine-concern-reference-block">
          <div className="skin-routine-concern-preview-panel">
            <div className="skin-routine-concern-preview-copy">
              <div className="skin-routine-concern-preview-header skin-routine-concern-preview-header--copy">
                <div>
                  <h4 className="skin-routine-concern-preview-title">
                    {selectedConcernItem
                      ? sanitizeText(selectedConcernItem.label)
                      : 'Concern preview'}
                  </h4>
                  {selectedConcernItem && (
                    <div
                      className="skin-routine-concern-preview-score-wrap skin-routine-concern-preview-score-wrap--copy"
                      role="img"
                      aria-label={`${displayScore} percent concern score`}
                    >
                      <div className="skin-routine-concern-gauge-wrapper">
                        <canvas
                          ref={concernGaugeCopyRef}
                          className="skin-routine-concern-gauge-canvas"
                          width="400"
                          height="280"
                        />
                        <div className="skin-routine-concern-gauge-display">
                          <div className="skin-routine-concern-gauge-label">Concern Score</div>
                          <div className="skin-routine-concern-gauge-value">{displayScore}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {selectedConcernItem && (
                  <span className={`skin-routine-detailed-severity skin-routine-severity-${scoreCategory}`}>
                    {severityLabel(displayScore)}
                  </span>
                )}
              </div>
              <p className="skin-routine-concern-preview-text skin-routine-concern-preview-text--copy">
                {selectedConcernItem ? (
                  <>
                    <strong>{sanitizeText(selectedConcernItem.label)}:</strong>{' '}
                    {selectedConcernDescription || 'No additional description available.'}
                  </>
                ) : (
                  'Select any concern below to inspect its focused preview and severity in the same flow as the results page.'
                )}
              </p>
            </div>

            <div className="skin-routine-concern-preview-media">
              <div
                className="skin-routine-concern-preview-frame"
                ref={containerRef}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
              >
                {showAngleNavigation && (
                  <>
                    <div className="skin-routine-preview-arrow-wrap skin-routine-preview-arrow-wrap--left">
                      <button
                        type="button"
                        className="skin-routine-preview-arrow"
                        onClick={() => moveViewBy(-1)}
                        aria-label="View previous angle"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="15 18 9 12 15 6" />
                        </svg>
                      </button>
                      <span className="skin-routine-preview-arrow-label">Prev</span>
                    </div>
                    <div className="skin-routine-preview-angle-chip">
                      {imageView.charAt(0).toUpperCase() + imageView.slice(1)}
                    </div>
                    <div className="skin-routine-preview-arrow-wrap skin-routine-preview-arrow-wrap--right">
                      <button
                        type="button"
                        className="skin-routine-preview-arrow"
                        onClick={() => moveViewBy(1)}
                        aria-label="View next angle"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </button>
                      <span className="skin-routine-preview-arrow-label">Next</span>
                    </div>
                  </>
                )}

                {hydrating && (
                  <div className="skin-routine-summary-loading skin-result-preview-loading">
                    <div className="loading-spinner" />
                    <p className="skin-routine-summary-loading-text">Loading captured images…</p>
                  </div>
                )}

                {hydrateError && !hydrating && (
                  <div className="skin-routine-concern-preview-empty">{hydrateError}</div>
                )}

                {!hydrating && !imageUrl && !resolvedMaskUrl && (
                  <div className="skin-routine-concern-preview-empty">Preview unavailable</div>
                )}

                {!hydrating && showMaskPreview && (
                  <img
                    key={`mask-${selectedConcern}`}
                    src={resolvedMaskUrl}
                    alt={selectedConcernItem ? `${selectedConcernItem.label} mask preview` : 'Skin condition preview'}
                    className="skin-routine-concern-preview-image skin-result-concern-preview-image--fit"
                  />
                )}

                {!hydrating && !showMaskPreview && isSkinConditionConcern && imageUrl && (
                  <img
                    key={`condition-${selectedConcern}`}
                    src={imageUrl}
                    alt={selectedConcernItem ? `${selectedConcernItem.label} preview` : 'Skin condition preview'}
                    className="skin-routine-concern-preview-image skin-result-concern-preview-image--fit"
                  />
                )}

                {!hydrating && !showMaskPreview && !isSkinConditionConcern && imageUrl && (
                  <DetectionCanvas
                    key={`${imageView}-${selectedConcern || 'none'}`}
                    imageUrl={imageUrl}
                    detections={activeDetections}
                    selectedConcern={selectedConcern}
                    containerWidth={containerWidth}
                    containerHeight={containerHeight}
                    fitSquare
                  />
                )}

                {selectedConcernSeverityGradientStyle && (
                  <div
                    key={selectedConcernItem?.key || selectedConcern || 'severity'}
                    className="severity-bar-gradient-only skin-routine-concern-preview-severity"
                    style={selectedConcernSeverityGradientStyle}
                  >
                    <div
                      className="severity-bar-gradient"
                      style={{ background: selectedConcernSeverityGradientStyle['--gradient-bg'] }}
                    />
                    <div className="severity-bar-labels">
                      {severityBarLabels.triple ? (
                        <>
                          <span>{severityBarLabels.left}</span>
                          <span>{severityBarLabels.middle}</span>
                          <span>{severityBarLabels.right}</span>
                        </>
                      ) : (
                        <>
                          <span>{severityBarLabels.left}</span>
                          <span>{severityBarLabels.right}</span>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {selectedConcernItem && (
                  <div
                    className="skin-routine-concern-preview-score-wrap skin-routine-concern-preview-score-wrap--overlay"
                    role="img"
                    aria-label={`${displayScore} percent concern score`}
                  >
                    <div className="skin-routine-concern-preview-score-head">
                      <h4 className="skin-routine-concern-preview-title">
                        {sanitizeText(selectedConcernItem.label)}
                      </h4>
                      <span className={`skin-routine-detailed-severity skin-routine-severity-${scoreCategory}`}>
                        {severityLabel(displayScore)}
                      </span>
                    </div>
                    <div className="skin-routine-concern-mobile-compact-score" aria-hidden="true">
                      <span className="skin-routine-concern-mobile-compact-name">
                        {sanitizeText(selectedConcernItem.label)}
                      </span>
                      <span className="skin-routine-concern-mobile-compact-value">{displayScore}%</span>
                    </div>
                    <div className="skin-routine-concern-preview-score-row">
                      <div className="skin-routine-concern-gauge-wrapper">
                        <canvas
                          ref={concernGaugeOverlayRef}
                          className="skin-routine-concern-gauge-canvas"
                          width="400"
                          height="280"
                        />
                        <div className="skin-routine-concern-gauge-display">
                          <div className="skin-routine-concern-gauge-label">Concern Score</div>
                          <div className="skin-routine-concern-gauge-value">{displayScore}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <p className="skin-routine-concern-preview-text skin-routine-concern-preview-text--below-image">
                {selectedConcernItem ? (
                  <>
                    <strong>{sanitizeText(selectedConcernItem.label)}:</strong>{' '}
                    {selectedConcernDescription || 'No additional description available.'}
                  </>
                ) : (
                  'Select any concern below to inspect its focused preview and severity in the same flow as the results page.'
                )}
              </p>
            </div>
          </div>

          <div className="skin-routine-concern-mobile-hint">
            <p className="skin-routine-concern-mobile-hint-text">
              Swipe left/right to view all concerns
            </p>
          </div>

          <div className="skin-routine-detailed-concern-grid-wrapper">
            <button
              type="button"
              className="skin-routine-concern-nav-arrow left"
              aria-label="Scroll left"
              onClick={() => scrollConcernGrid(-1)}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
            <div className="skin-routine-detailed-concern-grid" ref={concernGridRef}>
              {detailedItems.map((item) => {
                const score = Math.max(0, Math.min(100, Number(item.score || 0)));
                const itemCategory = getScoreOutlineColor(score);
                const itemSeverity = severityLabel(score);
                return (
                  <div
                    key={item.key}
                    data-concern-name={item.key}
                    className={`skin-routine-detailed-card score-outline-${itemCategory}${selectedConcern === item.key ? ' is-selected' : ''}`}
                    style={{ '--selected-accent': item.color || '#38bdf8' }}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleSelectItem(item)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleSelectItem(item);
                      }
                    }}
                  >
                    <div className="skin-routine-detailed-card-top">
                      <span className="skin-routine-detailed-name" style={{ color: item.color || '#0f766e' }}>
                        {sanitizeText(item.label)}
                      </span>
                      <span className="skin-routine-detailed-score-num" style={{ color: item.color || '#0f766e' }}>
                        {score}%
                      </span>
                    </div>
                    <div className="skin-routine-detailed-card-bottom">
                      <span className={`skin-routine-detailed-severity skin-routine-severity-${itemCategory}`}>
                        {itemSeverity}
                      </span>
                      <span className="skin-routine-detailed-card-hint">
                        {item.is_condition ? 'Skin quality' : `${item.view} view`}
                      </span>
                    </div>
                    <div className="skin-routine-detailed-bar-wrap">
                      <div
                        className="skin-routine-detailed-bar-fill"
                        style={{ width: `${score}%`, background: item.color || '#0f766e' }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <button
              type="button"
              className="skin-routine-concern-nav-arrow right"
              aria-label="Scroll right"
              onClick={() => scrollConcernGrid(1)}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DetectionViewer;
